<script>
	import { createEventDispatcher, onMount } from 'svelte';
	import { normalizeTeacher } from './teacherNormalize.js';
	import { getApiBase } from './apiBase.js';

	export let selectedTeacher = null;
	export let placeholder = 'Search teachers by name or email...';

	const dispatch = createEventDispatcher();

	let teachers = [];
	let teachersLoaded = false;
	let loadError = '';
	let isLoadingTeachers = false;

	let searchTerm = '';
	let searchResults = [];
	let isSearching = false;
	let debounceTimer = null;

	async function loadTeachersOnce() {
		if (teachersLoaded || isLoadingTeachers) return;
		isLoadingTeachers = true;
		loadError = '';
		const token = localStorage.getItem('jwtToken');
		if (!token) {
			loadError = 'JWT token not found. Please log in.';
			isLoadingTeachers = false;
			return;
		}

		try {
			const apiBase = getApiBase();
			const response = await fetch(`${apiBase}/studentplayground/api/teachers-list/?include_inactive=1`, {
				method: 'GET',
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'application/json',
				},
			});

			if (!response.ok) {
				const data = await response.json().catch(() => ({}));
				throw new Error(data.message || `Failed to load teachers (${response.status})`);
			}

			const data = await response.json();
			teachers = data?.teachers || [];
			teachersLoaded = true;
		} catch (e) {
			loadError = e?.message || 'Failed to load teachers';
			teachers = [];
			teachersLoaded = false;
		} finally {
			isLoadingTeachers = false;
		}
	}

	function filterTeachers(term) {
		const q = term.toLowerCase();
		return teachers
			.map(normalizeTeacher)
			.filter(Boolean)
			.filter(t => {
				const name = (t.name || '').toLowerCase();
				const email = (t.email || '').toLowerCase();
				return name.includes(q) || email.includes(q) || String(t.id) === term;
			})
			.slice(0, 20);
	}

	function debouncedSearch(term) {
		if (debounceTimer) clearTimeout(debounceTimer);
		debounceTimer = setTimeout(async () => {
			await searchTeachers(term);
		}, 200);
	}

	async function searchTeachers(term) {
		isSearching = true;
		await loadTeachersOnce();
		if (!teachersLoaded) {
			searchResults = [];
			isSearching = false;
			return;
		}
		searchResults = filterTeachers(term);
		isSearching = false;
	}

	function selectTeacher(teacher) {
		dispatch('teacherSelected', teacher);
		searchResults = [];
	}

	function clearSelection() {
		dispatch('teacherCleared');
	}

	// Exposed method for parent: resolve a teacher by ID
	export async function resolveTeacherById(id) {
		const idNum = parseInt(String(id), 10);
		if (Number.isNaN(idNum)) return null;
		await loadTeachersOnce();
		if (!teachersLoaded) return null;
		return normalizeTeacher(teachers.find(t => t.id === idNum)) || null;
	}

	export async function refreshTeachers() {
		teachersLoaded = false;
		await loadTeachersOnce();
	}

	$: if (!selectedTeacher && searchTerm && searchTerm.trim().length >= 2) {
		debouncedSearch(searchTerm.trim());
	} else if (!selectedTeacher && (!searchTerm || searchTerm.trim().length < 2)) {
		if (debounceTimer) clearTimeout(debounceTimer);
		debounceTimer = null;
		searchResults = [];
	}

	onMount(() => {
		// Preload so deep-link resolution is snappy.
		loadTeachersOnce();
	});
</script>

<div class="teacher-search">
	{#if loadError}
		<div class="error">{loadError}</div>
	{/if}

	<div class="search-row">
		<input
			class="search-input"
			type="text"
			bind:value={searchTerm}
			placeholder={placeholder}
			disabled={!!selectedTeacher}
		/>
		{#if isSearching || isLoadingTeachers}
			<div class="spinner" aria-label="Searching">⌛</div>
		{/if}
		{#if selectedTeacher}
			<button type="button" class="change-btn" on:click={clearSelection}>Change</button>
		{/if}
	</div>

	{#if selectedTeacher}
		<div class="selected-card">
			<div class="name">{selectedTeacher.name || `${selectedTeacher.first_name || ''} ${selectedTeacher.last_name || ''}`.trim()}</div>
			{#if selectedTeacher.email}
				<div class="meta">{selectedTeacher.email}</div>
			{/if}
			<div class="meta">ID: {selectedTeacher.id}</div>
		</div>
	{/if}

	{#if searchResults.length > 0 && !selectedTeacher}
		<div class="results" role="listbox" aria-label="Teacher search results">
			{#each searchResults as teacher}
				<div
					class="result"
					role="option"
					tabindex="0"
					aria-selected="false"
					on:click={() => selectTeacher(teacher)}
					on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && selectTeacher(teacher)}
				>
					<div class="name">{teacher.name}</div>
					<div class="meta">{teacher.email || ''}</div>
				</div>
			{/each}
		</div>
	{:else if !selectedTeacher && !isSearching && !isLoadingTeachers && searchTerm && searchTerm.trim().length >= 2}
		<div class="no-results">No teachers found.</div>
	{/if}
</div>

<style>
	.teacher-search {
		position: relative;
		width: 100%;
	}

	.search-row {
		display: flex;
		gap: 10px;
		align-items: center;
	}

	.search-input {
		flex: 1;
		width: 100%;
		padding: 12px 14px;
		border: 1px solid #ccc;
		border-radius: 8px;
		font-size: 14px;
	}

	.search-input:disabled {
		background: #f7f7f7;
		cursor: not-allowed;
	}

	.spinner {
		font-size: 12px;
		color: #666;
	}

	.change-btn {
		padding: 10px 14px;
		border: 1px solid #ccc;
		background: white;
		border-radius: 8px;
		cursor: pointer;
		white-space: nowrap;
	}

	.selected-card {
		margin-top: 12px;
		padding: 12px 14px;
		border-radius: 8px;
		border: 1px solid #bcdcff;
		background: #e7f3ff;
	}

	.results {
		margin-top: 8px;
		border: 1px solid #e0e0e0;
		border-radius: 8px;
		background: white;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
		max-height: 320px;
		overflow: auto;
	}

	.result {
		padding: 10px 12px;
		border-bottom: 1px solid #f0f0f0;
		cursor: pointer;
		outline: none;
	}

	.result:hover,
	.result:focus {
		background: #f8f9fa;
	}

	.result:last-child {
		border-bottom: none;
	}

	.name {
		font-weight: 600;
		color: #222;
	}

	.meta {
		color: #666;
		font-size: 12px;
		margin-top: 2px;
	}

	.no-results {
		margin-top: 8px;
		padding: 12px 14px;
		border-radius: 8px;
		background: #f8f9fa;
		border: 1px solid #e0e0e0;
		color: #666;
	}

	.error {
		margin-bottom: 10px;
		padding: 10px 12px;
		border-radius: 8px;
		background: #ffebee;
		border: 1px solid #f5c6cb;
		color: #721c24;
	}
</style>

